<template>
  <div class="service-parts-container relative min-h-screen py-12">
    <!-- Background -->
    <div class="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 opacity-40"></div>
    <div class="absolute inset-0 bg-pattern opacity-5"></div>
    
    <!-- Header Section -->
    <div class="relative z-10 text-center mb-16">
      <h2 class="text-4xl md:text-6xl font-bold text-white mb-6">
        Spare <span class="text-primary">Parts</span> Available
      </h2>
      <p class="text-xl text-white/80 max-w-3xl mx-auto">
        Partial listing of spare parts we keep in stock for immediate availability
      </p>
    </div>

    <!-- Parts Grid -->
    <div class="relative z-10 max-w-6xl mx-auto px-6">
      <div class="parts-grid">
        <!-- Airline Fittings -->
        <div class="parts-item" ref="part1">
          <div class="parts-header">
            <div class="parts-icon">
              <font-awesome-icon :icon="['fas', 'plug']" />
            </div>
            <h3>Airline Fittings</h3>
          </div>
          <div class="parts-description">
            <p>Complete selection of airline fittings and connections for pneumatic systems, ensuring reliable air flow and connections throughout your trailer's operating systems.</p>
          </div>
        </div>

        <!-- Couplings and Actuators -->
        <div class="parts-item" ref="part2">
          <div class="parts-header">
            <div class="parts-icon">
              <font-awesome-icon :icon="['fas', 'cogs']" />
            </div>
            <h3>Couplings and Actuators</h3>
          </div>
          <div class="parts-description">
            <p>High-quality couplings and actuators for precise control and reliable operation of your tank trailer's mechanical systems and valve operations.</p>
          </div>
        </div>

        <!-- Ball Valves and Filters -->
        <div class="parts-item" ref="part3">
          <div class="parts-header">
            <div class="parts-icon">
              <font-awesome-icon :icon="['fas', 'filter']" />
            </div>
            <h3>Ball Valves and Filters</h3>
          </div>
          <div class="parts-description">
            <p>Durable ball valves and filtration systems designed for stainless steel tank trailers, ensuring clean product flow and reliable shut-off capabilities.</p>
          </div>
        </div>
        <!-- Heat System Fittings -->
        <div class="parts-item" ref="part4">
          <div class="parts-header">
            <div class="parts-icon">
              <font-awesome-icon :icon="['fas', 'thermometer-half']" />
            </div>
            <h3>Heat System Fittings</h3>
          </div>
          <div class="parts-description">
            <p>Specialized fittings for heated tank trailer systems, including steam coils, heat exchangers, and thermal management components for temperature-sensitive cargo.</p>
          </div>
        </div>

        <!-- Hoses and Steam Traps -->
        <div class="parts-item" ref="part5">
          <div class="parts-header">
            <div class="parts-icon">
              <font-awesome-icon :icon="['fas', 'water']" />
            </div>
            <h3>Hoses and Steam Traps</h3>
          </div>
          <div class="parts-description">
            <p>Industrial-grade hoses and steam trap systems for efficient fluid transfer and steam management in heated tank trailer applications.</p>
          </div>
        </div>

        <!-- Pressure Relief Valve -->
        <div class="parts-item" ref="part6">
          <div class="parts-header">
            <div class="parts-icon">
              <font-awesome-icon :icon="['fas', 'gauge']" />
            </div>
            <h3>Pressure Relief Valve</h3>
          </div>
          <div class="parts-description">
            <p>Safety-critical pressure relief valves designed to protect your tank trailer and cargo from overpressure situations, meeting all DOT safety requirements.</p>
          </div>
        </div>

        <!-- Thermometers and Pressure Gauges -->
        <div class="parts-item" ref="part7">
          <div class="parts-header">
            <div class="parts-icon">
              <font-awesome-icon :icon="['fas', 'tachometer-alt']" />
            </div>
            <h3>Thermometers and Pressure Gauges</h3>
          </div>
          <div class="parts-description">
            <p>Precision measurement instruments for monitoring temperature and pressure levels, essential for safe operation and cargo quality control.</p>
          </div>
        </div>
        <!-- Butterfly Valves and Compression Valves -->
        <div class="parts-item" ref="part8">
          <div class="parts-header">
            <div class="parts-icon">
              <font-awesome-icon :icon="['fas', 'faucet']" />
            </div>
            <h3>Butterfly Valves and Compression Valves</h3>
          </div>
          <div class="parts-description">
            <p>High-performance butterfly and compression valves for efficient flow control and tight sealing in demanding tank trailer applications.</p>
          </div>
        </div>

        <!-- Valve Wrenches -->
        <div class="parts-item" ref="part9">
          <div class="parts-header">
            <div class="parts-icon">
              <font-awesome-icon :icon="['fas', 'wrench']" />
            </div>
            <h3>Valve Wrenches</h3>
          </div>
          <div class="parts-description">
            <p>Specialized tools and wrenches designed for proper valve operation and maintenance, ensuring safe and efficient handling of tank trailer valves.</p>
          </div>
        </div>

        <!-- Gaskets and Vents -->
        <div class="parts-item" ref="part10">
          <div class="parts-header">
            <div class="parts-icon">
              <font-awesome-icon :icon="['fas', 'ring']" />
            </div>
            <h3>Gaskets and Vents</h3>
          </div>
          <div class="parts-description">
            <p>Comprehensive selection of gaskets, seals, and venting systems to maintain proper tank integrity and pressure management.</p>
          </div>
        </div>

        <!-- Manholes -->
        <div class="parts-item" ref="part11">
          <div class="parts-header">
            <div class="parts-icon">
              <font-awesome-icon :icon="['fas', 'door-open']" />
            </div>
            <h3>Manholes</h3>
          </div>
          <div class="parts-description">
            <p>Standard and custom manhole covers and assemblies for safe tank access, cleaning, and inspection procedures.</p>
          </div>
        </div>
        <!-- Placard Holders -->
        <div class="parts-item" ref="part12">
          <div class="parts-header">
            <div class="parts-icon">
              <font-awesome-icon :icon="['fas', 'sign']" />
            </div>
            <h3>Placard Holders</h3>
          </div>
          <div class="parts-description">
            <p>DOT-compliant placard holders and mounting systems for proper hazardous material identification and regulatory compliance.</p>
          </div>
        </div>

        <!-- Landing Gear Parts -->
        <div class="parts-item" ref="part13">
          <div class="parts-header">
            <div class="parts-icon">
              <font-awesome-icon :icon="['fas', 'triangle-exclamation']" />
            </div>
            <h3>Landing Gear Parts</h3>
          </div>
          <div class="parts-description">
            <p>Complete landing gear components including legs, gears, handles, and mounting hardware for reliable trailer support and positioning.</p>
          </div>
        </div>
      </div>
      
      <!-- Contact CTA -->
      <div class="parts-cta text-center mt-16">
        <h3 class="text-2xl md:text-3xl font-bold text-white mb-4">
          Need a specific part?
        </h3>
        <p class="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
          Our parts inventory extends beyond this list. Contact us for availability on any tank trailer component.
        </p>
        <button class="contact-btn">
          <span>Contact Parts Department</span>
          <font-awesome-icon :icon="['fas', 'phone']" />
        </button>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  name: 'ServicePartsSection',
  mounted() {
    this.setupIntersectionObserver();
  },
  beforeUnmount() {
    if (this.observers) {
      this.observers.forEach(observer => observer.disconnect());
    }
  },
  data() {
    return {
      observers: []
    };
  },
  methods: {
    setupIntersectionObserver() {
      for (let i = 1; i <= 13; i++) {
        const partRef = this.$refs[`part${i}`];
        if (partRef) {
          const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                setTimeout(() => {
                  entry.target.classList.add('animate-in');
                }, (i - 1) * 100);
              }
            });
          }, { threshold: 0.1 });
          
          observer.observe(partRef);
          this.observers.push(observer);
        }
      }
    }
  }
};
</script>
<style scoped>
.bg-pattern {
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.parts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
}

.parts-item {
  display: flex;
  flex-direction: column;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  opacity: 0;
  transform: translateY(30px);
  backdrop-filter: blur(8px);
}

.parts-item.animate-in {
  opacity: 1;
  transform: translateY(0);
}

.parts-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
  border-color: rgba(162, 42, 33, 0.3);
  background: rgba(255, 255, 255, 0.04);
}

.parts-header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.parts-icon {
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #a22a21, #c23529);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  transition: transform 0.3s ease;
}

.parts-item:hover .parts-icon {
  transform: scale(1.1) rotate(5deg);
}

.parts-header h3 {
  color: white;
  font-size: 1.25rem;
  font-weight: bold;
  margin: 0;
}

.parts-description p {
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  font-size: 0.95rem;
  margin: 0;
}

.parts-cta {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 3rem 2rem;
  backdrop-filter: blur(10px);
}

.contact-btn {
  background: linear-gradient(135deg, #a22a21, #c23529);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
}

.contact-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(162, 42, 33, 0.4);
}

@media (max-width: 768px) {
  .parts-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .parts-item {
    padding: 1.5rem;
  }
  
  .parts-header {
    gap: 1rem;
  }
  
  .parts-icon {
    width: 50px;
    height: 50px;
    font-size: 1.25rem;
  }
  
  .parts-header h3 {
    font-size: 1.1rem;
  }
}
</style>